/** @format */

import {HubConnectionBuilder, LogLevel} from '@microsoft/signalr';
import $cs_ from '@/js/plugins/cs-underscore-helpers';

//INTERFACE
//PRIVATE
let $_subscribedChannels = {};
let _subscribedChannelsData = {};

//PUBLIC 

//IMPLEMENTATION
export const $subscriptionData = {};

/**
 * @public
 * @description
 * Gets any data we may need for subsequent push notifications
 *
 * @param {String} channelPath The channel path to listen to
 * @param {String} pushEventName The event to listen to for from the hub - will combine with channelPath to make a unique key
 *
 * @returns {Void}
 */
$subscriptionData.getConfiguredSubscriptionData = (channelPath, pushEventName) => {
	const uniqueKey = `${channelPath}-${pushEventName}`;
	return _subscribedChannelsData[uniqueKey];
};

/**
 * @public
 * @description
 * Deletes any data we may have set for specific push notifications
 *
 * @param {String} channelPath The channel path to listen to
 * @param {String} pushEventName The event to listen to for from the hub - will combine with channelPath to make a unique key
 *
 * @returns {Void}
 */
$subscriptionData.deleteConfiguredSubscriptionData = function (channelPath, pushEventName) {
	const uniqueKey = `${channelPath}-${pushEventName}`;
	if (_subscribedChannelsData[uniqueKey]) {
		delete _subscribedChannelsData[uniqueKey];
	}
};

/**
 * @public
 * @description
 * Adds any data we may need for subsequent push notifications
 *
 * @param {String} channelPath The channel path to listen to
 * @param {String} pushEventName The event to listen to for from the hub - will combine with channelPath to make a unique key
 * @param {Object} data The data we wish to use for the subscription
 *
 * @returns {Void}
 */
$subscriptionData.setConfiguredSubscriptionData = function (channelPath, pushEventName, data) {
	const uniqueKey = `${channelPath}-${pushEventName}`;
	_subscribedChannelsData[uniqueKey] = $cs_.deepClone(data);
};

const _privateGetChannelId = function (channelPath, channelQueryString) {
	const jobId = channelQueryString.slice(channelQueryString.lastIndexOf('=') + 1);
	return `${channelPath}${jobId.length > 0 ? '-' : ''}${jobId}`;
};

/**
 * @public
 * @description
 * subscribes to an update channel from the api and signalr
 *
 * @param {String} channelName The channel path to listen to
 * @param {String} channelQueryString Optional querystring to add to the channel request
 * @param {String} pushEventName The event to listen to for from the hub
 * @param {String} componentName The component name to register as a subscriber
 * @param {String} componentMethodName The method in the subscriber to call when the event occurs
 * @param { Boolean } startConnectionImmediately whenever to start connection immediately after subcribing to the hub
 *
 * @returns {Boolean}
 */
export const $subscribeToUpdates = function (channelName, channelQueryString = '', pushEventName, componentName, componentMethodName, startConnectionImmediately = false, stopConnectionAfterData = false, token) {
	// TODO: Left commented out in case of regression. However I coudn't find a place we reuse recivied earlier data with a new subscription request.
	// if ($_subscribedChannels[channelName]) {
	// 	return $cs_.get($_subscribedChannels[channelName], 'lastReceivedData', pushEventName);
	// }

	const channelId = _privateGetChannelId(channelName, channelQueryString);
	const apiBaseUrl = $cs_.getAppNode().$getApiBaseUrl();
	//modify code to include new notification center code where token is provided
	if (token == undefined) {
		$_subscribedChannels[channelId] = {
			lastReceivedData: {},
			connection: new HubConnectionBuilder().withUrl(`${apiBaseUrl}/${channelName}${channelQueryString}`).withAutomaticReconnect().build()
		};
	} else {
		$_subscribedChannels[channelId] = {
			lastReceivedData: {},
			connection: new HubConnectionBuilder().withUrl(`${apiBaseUrl}/${channelName}${channelQueryString}`, {accessTokenFactory: () => token}).configureLogging(LogLevel.Information).withAutomaticReconnect().build()
		};
	}
	$_subscribedChannels[channelId].connection.keepAliveIntervalInMilliseconds = 60000 * 2;
	$_subscribedChannels[channelId].connection.serverTimeoutInMilliseconds = 60000 * 4;

	if (startConnectionImmediately === true) $startConnection(channelId);

	$_subscribedChannels[channelId].connection.on(pushEventName, (data) => {
		if (componentName != undefined) {
			let listeners = $cs_.querySelectorAllVue($cs_.getAppNode(), {name: componentName, dataKey: 'listeningOnChannel', dataValue: channelName});
			for (let listener of listeners) {
				listener[componentMethodName](data);
			}
			$_subscribedChannels[channelId].lastReceivedData[pushEventName] = data;
		} 
	});
};

/**
 * @public
 * @description
 * Stops listening to all connections with given channel name
 *
 * @param {String} channelName
 */
export const $stopListeningToUpdates = async function (channelName) {
	for (let channelId of Object.keys($_subscribedChannels)) {
		if (channelId.includes(channelName)) {
			await $_subscribedChannels[channelId].connection.stop();
			delete $_subscribedChannels[channelId];
		}
	}
};

/**
 * @public
 * @description
 * Starts a connection with the hub channel
 *
 * @param { String } channelId a unique id
 * @param { Function } successCallback a callback function to execute after successful response from the channel. NOTE! Please be aware what you pass with closure
 */
export const $startConnection = async function (channelId, successCallback, attempts = 0) {
	try {
		await $_subscribedChannels[channelId].connection.start();
		if (typeof successCallback == 'function') successCallback();
	} catch (err) {
		console.error('Failed to start connection with the hub.', err);
		if (attempts < 4) setTimeout(() => $startConnection(channelId, successCallback, attempts + 1), 5000); // Attempt to connect 5 times then abandon
	}
};

/**
 * @public
 * @description
 * Stops a connection with the hub channel
 *
 * @param { String } channelId a unique id
 * @param { Function } successCallback a callback function to execute after successful response from the channel. NOTE! Please be aware what you pass with closure
 */
export const $stopConnection = async function (channelId, successCallback) {
	try {
		await $_subscribedChannels[channelId].connection.stop();
		if (typeof successCallback == 'function') successCallback();
		return true;
	} catch (err) {
		console.error('Failed to disconnect from the hub.', err);
	}
};

/**
 * @public
 * @description
 * Sends message through the hub channel
 *
 * @param { String } channelId a unique id
 * @param { String } message a message name for the push notification
 * @param { Array } params an array of given parameters for the notification
 * @param { Function } successCallback a callback function to execute after successful response from the channel. NOTE! Please be aware what you pass with closure
 */
export const $updateConnection = async function (channelId, message, params, successCallback) {
	try {
		if ($_subscribedChannels[channelId] != undefined) {
			await $_subscribedChannels[channelId].connection.invoke(message, ...params);
			if (typeof successCallback == 'function') successCallback();
		}
	} catch (err) {
		console.error('Failed to send message to the hub.', err);
	}
};
